import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
// Context provider for onboarding flow state management
import { supabase } from '@/integrations/supabase/client';
import { AUTH_ONBOARDING_RESUME_KEY } from '@/constants/onboardingResume';

export type AccountType = 'individual' | 'corporate';

export type OnboardingStep = 
  | 'welcome'
  | 'account-type'
  // Individual flow
  | 'phone'
  | 'individual-declarations'
  | 'email'
  | 'personal-info'
  | 'date-of-birth'
  | 'employment'
  | 'income'
  | 'payment-destinations'
  | 'document-upload'
  | 'document-details-confirmation'
  | 'selfie'
  | 'document-signing'
  | 'passcode'
  | 'final-declarations'
  | 'success'
  // Corporate flow (new company-first structure)
  | 'corporate-company-search'
  | 'corporate-existing-check'
  | 'corporate-company-details'
  | 'corporate-services'
  | 'corporate-phone'
  | 'corporate-email'
  | 'corporate-passcode'
  | 'corporate-business-activity'
  | 'corporate-payment-flows'
  | 'corporate-directors-shareholders'
  | 'corporate-ubo-trace'
  | 'corporate-authorized-person'
  | 'corporate-individual-declarations'
  | 'corporate-biometrics'
  | 'corporate-baas'
  | 'corporate-documents'
  | 'corporate-document-signing'
  | 'corporate-success'
  // Legacy steps (kept for migration/compatibility)
  | 'corporate-directors'
  | 'corporate-shareholders'
  | 'corporate-business-questionnaire'
  | 'corporate-tax-residency'
  | 'corporate-pep-declarations'
  | 'corporate-document-upload';

interface TaxCountry {
  code: string;
  name: string;
  flag: string;
  tin?: string;
}

interface PaymentDestination {
  code: string;
  name: string;
  flag: string;
}

interface CorporateAddress {
  street: string;
  city: string;
  postalCode: string;
  country: string;
}

export interface PersonDeclarationData {
  isPEP?: boolean;
  pepDetails?: string;
  taxResidencies?: Array<{ code: string; name: string; flag: string; tin?: string }>;
  isUSResident?: boolean;
  giinNumber?: string;
  declarationsCompleted?: boolean;
}

interface CorporateDirector extends PersonDeclarationData {
  name: string;
  dateOfBirth: string;
  nationality: string;
  address: CorporateAddress;
  appointedDate: string;
  occupation: string;
}

interface CorporateUBO {
  name: string;
  shareholding: number;
  address: CorporateAddress;
  type: 'individual' | 'company';
  registrationNumber?: string;
  ubos?: CorporateUBO[]; // Recursive nesting
}

interface CorporateShareholder extends PersonDeclarationData {
  name: string;
  shareholding: number;
  shareClass: string;
  address: CorporateAddress;
  type: 'individual' | 'company';
  ubos?: CorporateUBO[];
  registrationNumber?: string;
  isManuallyAdded?: boolean;
}

interface CorporateSicCode {
  code: string;
  description: string;
}

interface CorporateAuthorizedPerson {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  phoneCountryCode?: string;
  jobTitle: string;
  dateOfBirth?: string;
  nationality?: string;
  address?: CorporateAddress;
  isDirector: boolean;
  isExistingPerson?: boolean;
  // Document details from Sumsub ID verification
  documentType?: string;
  documentNumber?: string;
  documentIssueDate?: string;
  documentExpiry?: string;
  documentCountry?: string;
}

interface CorporateBusinessQuestionnaire {
  expectedMonthlyVolume: string;
  expectedYearlyTurnover: string;
  sourceOfFunds: string[];
  otherSourceOfFunds?: string;
  sourceOfWealth: string[];
  otherSourceOfWealth?: string;
  countriesOfOperation: string[];
  purposeOfAccount: string[];
  otherPurpose?: string;
  holdsClientFunds: boolean;
}

export interface BaaSApplicationData {
  // Section A: Regulatory
  isRegulated: boolean | null;
  regulatorName?: string;
  licenceNumber?: string;
  licencePermissions?: string[];
  otherPermission?: string;
  // Section B: MLRO
  mlroFullName?: string;
  mlroEmail?: string;
  mlroPhone?: string;
  mlroPhoneCountryCode?: string;
  mlroIsExistingPerson?: boolean | null;
  mlroLinkedDirectorIndex?: number | null;
  // Section C: Technical
  clientFundsHandling?: string;
  scaMethods?: string[];
  expectedClientsCount?: string;
  expectedMonthlyVolume?: string;
  // Section D: AML Policy
  amlPolicyComponents?: string[];
  // Document auto-fetch flags (set by document step)
  incorporationDocFetched?: boolean;
  articlesDocFetched?: boolean;
}

interface CorporateCompany {
  id: string;
  connectId?: string; // CreditSafe connect ID for re-fetching
  name: string;
  registrationNumber: string;
  registeredAddress: string;
  status?: string;
  incorporationDate?: string;
  companyType?: string;
  fullAddress?: CorporateAddress;
  sicCodes?: CorporateSicCode[];
  directors?: CorporateDirector[];
  shareholders?: CorporateShareholder[];
  countryCode?: string;
  businessDescription?: string;
  websiteUrl?: string;
  authorizedPerson?: CorporateAuthorizedPerson;
  businessQuestionnaire?: CorporateBusinessQuestionnaire;
  isManualEntry?: boolean;
  manualIndustries?: string[];
  mainActivity?: string; // Fallback from CreditSafe when SIC codes unavailable (original language)
  mainActivityEnglish?: string; // AI-translated English version of mainActivity
  mainActivityCode?: string; // Activity code (CAE, SIC, NACE, etc.)
  // Authorized person contact details (pre-populated from step 10)
  authorizedPersonContact?: {
    selectedDirectorIndex: number | null;
    isApplicant: boolean;
    email: string;
    phoneCountryCode: string;
    phoneNumber: string;
  };
  // VAT and Tax registration
  vatRegistered?: boolean;
  vatSameAsRegistration?: boolean;
  vatNumber?: string;
  taxNumber?: string; // UTR or equivalent
  taxNumberSameAsRegistration?: boolean;
  // Services and currency accounts
  servicesRequested?: string[]; // business_account, pos_terminal, online_card_processing, corporate_fx, international_payments
  currencyAccounts?: string[]; // EUR (mandatory) + 2 additional currencies
}

interface CorporatePaymentFlow {
  types: string[];
  otherDescription?: string;
  monthlyVolume: string;
  monthlyCount: string; // Number of expected monthly transactions
  countries: { code: string; name: string; flag: string }[];
}

interface CorporatePaymentFlows {
  incoming?: CorporatePaymentFlow;
  outgoing?: CorporatePaymentFlow;
}

interface CorporatePepDeclaration {
  id: string;
  name: string;
  type: 'director' | 'shareholder' | 'authorized';
  shareholding?: number;
  completed: boolean;
  isPEP?: boolean;
  pepDetails?: string;
}

interface CorporateBiometric {
  id: string;
  name: string;
  type: 'director' | 'shareholder' | 'authorized';
  shareholding?: number;
  completed: boolean;
  email?: string;
  phone?: string;
  linkSentVia?: 'email' | 'sms' | 'whatsapp';
  linkSentAt?: string;
}

interface CorporateTaxResidency {
  id: string;
  name: string;
  type: 'director' | 'shareholder' | 'authorized';
  shareholding?: number;
  taxResidencies: Array<{ code: string; name: string; flag: string; tin?: string }>;
  isUSResident: boolean;
  giinNumber?: string;
  completed: boolean;
}

interface OnboardingData {
  // Account type
  accountType: AccountType;
  // Individual fields
  countryCode: string;
  phoneNumber: string;
  phoneOtpVerified?: boolean; // true only after OTP code was successfully confirmed
  countryOfResidence: string;
  email: string;
  emailOtpVerified?: boolean; // true only after email OTP code was successfully confirmed
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  tradingAddress?: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  // Consent
  dataProcessingConsent?: boolean;
  marketingConsent?: boolean;
  // PEP Declaration
  isPEP: boolean;
  pepDetails: string;
  // Employment
  employmentStatus: string;
  industry: string;
  employerDetails?: {
    name: string;
    address: string;
    jobTitle: string;
  };
  businessDetails?: {
    name: string;
    registrationNumber: string;
  };
  // Income
  annualIncome: string;
  sourceOfFunds: string[];
  otherSourceOfFunds?: string;
  // Tax Residency
  taxResidencies: TaxCountry[];
  isUSResident: boolean;
  giinNumber?: string;
  // Payment Destinations
  paymentDestinations: PaymentDestination[];
  // KYC - extracted data from document verification
  documentType: string;
  documentUploaded: boolean;
  selfieCompleted: boolean;
  extractedFirstName?: string;
  extractedLastName?: string;
  extractedDateOfBirth?: string;
  extractedAddress?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  // Address as confirmed by user in document details step
  confirmedAddress?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  // Document details for compliance
  extractedDocumentNumber?: string;
  extractedDocumentExpiry?: string;
  extractedDocumentIssueDate?: string;
  extractedDocumentCountry?: string;
  extractedNationality?: string;
  documentDetailsConfirmed?: boolean;
  // Proof of Address
  poaVerified: boolean;
  poaDocumentType?: string;
  poaExtractedAddress?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  poaIssueDate?: string;
  poaIssuer?: string;
  // Verification status and failure tracking
  verificationFailed?: boolean;
  verificationFailureReason?: string;
  verificationFailureCategory?: 'biometrics' | 'document' | 'aml' | 'other';
  verificationStatus?: 'passed' | 'failed' | 'pending';
  // Biometric check results from Sumsub
  biometrics?: {
    liveness?: {
      status: 'passed' | 'failed' | 'pending';
      confidence?: number;
    };
    faceMatch?: {
      status: 'passed' | 'failed' | 'pending';
      confidence?: number;
    };
  };
  // AML Screening data from Sumsub
  amlScreening?: {
    pepMatch?: boolean;
    pepCategory?: string;
    sanctionsMatch?: boolean;
    adverseMediaMatch?: boolean;
    adverseMediaScore?: number;
    adverseMediaDetails?: Array<{
      source?: string;
      date?: string;
      title?: string;
      severity?: string;
    }>;
    overallRisk?: string;
    screeningDate?: string;
  };
  // Document Signing
  documentsSignedAt: string;
  signedDocuments: string[];
  documentSignature?: string; // Base64 signature image
  // Passcode
  passcode: string;
  // Corporate fields
  corporateCompany?: CorporateCompany;
  // Corporate PEP declarations for directors/shareholders
  corporatePepDeclarations?: CorporatePepDeclaration[];
  // Corporate biometrics verifications (for UBOs with ≥25% effective ownership)
  corporateBiometrics?: CorporateBiometric[];
  // Corporate tax residencies
  corporateTaxResidencies?: CorporateTaxResidency[];
  // Company branding (logo, colors) extracted from website
  companyBranding?: {
    logo?: string;
    colors?: {
      primary?: string;
      secondary?: string;
      background?: string;
    };
    favicon?: string;
  };
  companyLogo?: string;
  // Corporate payment flows (source of funds, destinations)
  corporatePaymentFlows?: CorporatePaymentFlows;
  // BaaS application data (only populated when BaaS service is selected)
  baasApplicationData?: BaaSApplicationData;
}

interface OnboardingContextType {
  currentStep: OnboardingStep;
  setCurrentStep: (step: OnboardingStep) => void;
  data: OnboardingData;
  updateData: (updates: Partial<OnboardingData>) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  stepIndex: number;
  totalSteps: number;
  customBackHandler: (() => boolean) | null;
  setCustomBackHandler: (handler: (() => boolean) | null) => void;
  restoreSession: (sessionData: {
    currentStep: string;
    accountType: string;
    onboardingData: Record<string, unknown>;
    sessionToken: string;
    email: string;
  }) => void;
  resetOnboarding: () => void;
  sessionToken: string | null;
  duplicateBlock: { message: string; email: string | null; action: 'login' | 'resume' | 'contact_support'; } | null;
  clearDuplicateBlock: () => void;
}

// Individual onboarding steps - Tax + PEP declarations moved after Sumsub biometrics
// Steps that are biometric / Sumsub identity verification — bypassed for BaaS partner manual ops
// (partners have already taken biometrics from the client)
export const BIOMETRIC_STEPS = new Set<OnboardingStep>([
  'document-upload',
  'document-details-confirmation',
  'corporate-biometrics',
]);

export const individualSteps: OnboardingStep[] = [
  'welcome',
  'account-type',
  'phone',
  'email',
  'passcode',
  'document-upload',              // Sumsub biometrics
  'document-details-confirmation',
  'individual-declarations',      // Tax + PEP after biometrics
  'employment',
  'income',
  'payment-destinations',
  'document-signing',
  'final-declarations',
  'success'
];

// Corporate onboarding steps - NEW COMPANY-FIRST FLOW
// 1. Company search first (CreditSafe)
// 2. Existing application check → resume or create
// 3. Confirm company details (prefilled)
// 4-6. Phone/Email/Password (reuse individual components)
// 7. Business activity (website AI)
// 8. Directors & shareholders review (merged)
// 9. UBO trace (≥25%)
// 10. Authorized person
// 11. Biometrics — Sumsub dispatch (send links / verify in-person)
// 12. Documents (auto-pull + conditional)
// 13. Submit
export const corporateSteps: OnboardingStep[] = [
  'welcome',                          // index 0
  'account-type',                     // index 1
  'corporate-company-search',         // index 2  → UI Step 1
  'corporate-company-details',        // index 3  → UI Step 2
  'corporate-phone',                  // index 4  → UI Step 3
  'corporate-email',                  // index 5  → UI Step 4
  'corporate-passcode',               // index 6  → UI Step 5
  'corporate-business-activity',      // index 7  → UI Step 6
  'corporate-services',               // index 8  → UI Step 7
  'corporate-payment-flows',          // index 9  → UI Step 8
  'corporate-baas',                   // index 10 → UI Step 9  (only shown if BaaS selected)
  'corporate-directors-shareholders', // index 11 → UI Step 10
  'corporate-ubo-trace',              // index 12 → UI Step 11 (Sumsub for everyone)
  'corporate-individual-declarations',// index 13 → UI Step 12 (PEP/Tax/FATCA — AFTER Sumsub)
  'corporate-documents',              // index 14 → UI Step 13
  'corporate-document-signing',       // index 15 → UI Step 14
  'final-declarations',               // index 16 → UI Step 15 (Submission)
  'corporate-success'                 // index 17
];

const initialData: OnboardingData = {
  accountType: 'individual',
  countryCode: '+44',
  phoneNumber: '',
  countryOfResidence: '',
  email: '',
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  address: {
    street: '',
    city: '',
    postalCode: '',
    country: '', // No default - will use countryOfResidence or extracted data
  },
  isPEP: false,
  pepDetails: '',
  employmentStatus: '',
  industry: '',
  employerDetails: undefined,
  businessDetails: undefined,
  annualIncome: '',
  sourceOfFunds: [],
  taxResidencies: [],
  isUSResident: false,
  giinNumber: '',
  paymentDestinations: [],
  documentType: '',
  documentUploaded: false,
  selfieCompleted: false,
  poaVerified: false,
  documentsSignedAt: '',
  signedDocuments: [],
  passcode: '',
  corporateCompany: undefined,
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

function normalizeRestoredStep(rawStep: string, accountType: AccountType, data?: OnboardingData): OnboardingStep {
  const allowed = accountType === 'corporate' ? corporateSteps : individualSteps;

  let step = rawStep as OnboardingStep;

  // Migration/back-compat: map old corporate steps to new flow
  if (accountType === 'corporate') {
    if (rawStep === 'phone') step = 'corporate-phone';
    else if (rawStep === 'email') step = 'corporate-email';
    else if (rawStep === 'country-residence') step = 'corporate-company-search';
    else if (rawStep === 'corporate-directors') step = 'corporate-directors-shareholders';
    else if (rawStep === 'corporate-shareholders') step = 'corporate-directors-shareholders';
    else if (rawStep === 'corporate-tax-residency') step = 'corporate-ubo-trace';
    else if (rawStep === 'corporate-pep-declarations') step = 'corporate-ubo-trace';
    else if (rawStep === 'corporate-business-questionnaire') step = 'corporate-business-activity';
    else if (rawStep === 'corporate-document-upload') step = 'corporate-documents';
    else if (rawStep === 'corporate-biometrics') step = 'corporate-ubo-trace';
    else if (rawStep === 'corporate-authorized-person') step = 'corporate-ubo-trace';
    // 'corporate-individual-declarations' is now a real step — no migration needed
  }

  if (!allowed.includes(step)) {
    // Migration for individual flow
    if (rawStep === 'address' && accountType === 'individual') return 'individual-declarations';
    if (rawStep === 'tax-residency' && accountType === 'individual') return 'payment-destinations';
    if ((rawStep === 'country-residence' || rawStep === 'pep-declaration') && accountType === 'individual') return 'individual-declarations';
    return 'welcome';
  }

    // For corporate sessions, the DB's current_step is the source of truth.
    // We only skip auth-gate steps (existing-check, phone, email, passcode)
    // since the user already authenticated to get here.
    // We do NOT aggressively infer forward — doing so caused users to be sent
    // past the step they were actually on (e.g., directors → ubo-trace).
    if (accountType === 'corporate' && data) {
      // Skip the auth-gate steps — user has proven identity via password
      if (step === 'corporate-existing-check') {
        step = 'corporate-company-details'; // legacy migration: step no longer in array
      }
      // Auth-gate steps are skipped dynamically by goToNextStep when already completed.
      // Don't jump past them here — the user may need to complete them.

      // Only use data inference as a fallback when the DB step is very early
      // (e.g., the step was never saved properly, stuck at welcome/account-type)
      const isVeryEarly = step === 'welcome' || step === 'account-type' || step === 'corporate-company-search';
      if (isVeryEarly) {
        const company = data.corporateCompany;
        const hasCompanyDetails = Boolean(company?.registrationNumber);
        const hasBusinessActivity = Boolean(company?.businessDescription);
        const hasServices = Boolean(company?.servicesRequested?.length);
        const hasDirectors = Boolean(company?.directors?.length || company?.shareholders?.length);
        const hasAuthorizedPerson = Boolean(company?.authorizedPerson);

        let inferredStep: OnboardingStep = step;
        if (hasCompanyDetails) inferredStep = 'corporate-company-details';
        if (hasBusinessActivity) inferredStep = 'corporate-business-activity';
        if (hasServices) inferredStep = 'corporate-services';
        if (hasDirectors) inferredStep = 'corporate-directors-shareholders';
        if (hasAuthorizedPerson) inferredStep = 'corporate-ubo-trace';

        const dbStepIndex = allowed.indexOf(step);
        const inferredStepIndex = allowed.indexOf(inferredStep);
        if (inferredStepIndex > dbStepIndex) {
          step = inferredStep;
        }
      }

      
    }

  return step;
}

const STORAGE_KEY = 'paynovus_onboarding_progress';

function hasResumeCredential(data: {
  passcode?: unknown;
  hashed_passcode?: unknown;
  hashedPasscode?: unknown;
  password_hash?: unknown;
  resumeAuthenticated?: unknown;
} | null | undefined) {
  return Boolean(
    data?.passcode ||
    data?.hashed_passcode ||
    data?.hashedPasscode ||
    data?.password_hash ||
    data?.resumeAuthenticated
  );
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [currentStep, _setCurrentStep] = useState<OnboardingStep>('welcome');
  const [data, setData] = useState<OnboardingData>(initialData);
  const [isRestored, setIsRestored] = useState(false);
  // IMPORTANT: React state setters treat functions as updaters.
  // We store a function in state, so wrap it to avoid accidental execution.
  const [customBackHandler, _setCustomBackHandler] = useState<(() => boolean) | null>(null);
  const setCustomBackHandler = useCallback((handler: (() => boolean) | null) => {
    _setCustomBackHandler(handler ? (() => handler) : null);
  }, []);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSaveRef = useRef<string>('');

  // Duplicate / identity block state — set when save-onboarding-progress returns 409 with duplicate_identity
  const [duplicateBlock, setDuplicateBlock] = useState<{
    message: string;
    email: string | null;
    action: 'login' | 'resume' | 'contact_support';
  } | null>(null);

  // Wrapper for setCurrentStep — NEVER clears data on navigation
  // Data must persist across all steps including back-navigation to welcome/account-type
  const setCurrentStep = (step: OnboardingStep) => {
    _setCurrentStep(step);
  };

  const applyRestoredSession = useCallback((sessionData: {
    currentStep: string;
    accountType: string;
    onboardingData: Record<string, unknown>;
    sessionToken?: string | null;
    email?: string;
  }) => {
    const restoredData = {
      ...initialData,
      ...sessionData.onboardingData,
      email: sessionData.email || (sessionData.onboardingData.email as string | undefined) || '',
      accountType: sessionData.accountType as AccountType
    };

    const restoredAccountType = sessionData.accountType as AccountType;
    const fullRestoredData = {
      ...(restoredData as OnboardingData),
      accountType: restoredAccountType,
    };

    const targetStep = normalizeRestoredStep(sessionData.currentStep, restoredAccountType, fullRestoredData);

    const allowedSteps = restoredAccountType === 'corporate' ? corporateSteps : individualSteps;
    const stepIdx = allowedSteps.indexOf(targetStep);
    const phoneIdx = Math.max(allowedSteps.indexOf('corporate-phone'), allowedSteps.indexOf('phone'));
    const emailIdx = Math.max(allowedSteps.indexOf('corporate-email'), allowedSteps.indexOf('email'));
    if (stepIdx > phoneIdx && fullRestoredData.phoneNumber) fullRestoredData.phoneOtpVerified = true;
    if (stepIdx > emailIdx && fullRestoredData.email && !fullRestoredData.email.includes('@pending.local')) fullRestoredData.emailOtpVerified = true;

    setData(fullRestoredData);
    setSessionToken(sessionData.sessionToken || null);
    setCurrentStep(targetStep);
  }, []);

  // On mount, restore any in-progress session from localStorage.
  // RULE: Only restore if the session has a password set (hashed_passcode or passcode).
  //       Before a password is created, a page refresh always starts fresh.
  useEffect(() => {
    try {
      const authResume = sessionStorage.getItem(AUTH_ONBOARDING_RESUME_KEY);
      if (authResume) {
        sessionStorage.removeItem(AUTH_ONBOARDING_RESUME_KEY);
        const parsed = JSON.parse(authResume);
        if (parsed?.onboardingData && parsed?.currentStep) {
          applyRestoredSession({
            currentStep: parsed.currentStep,
            accountType: parsed.accountType || parsed.onboardingData.accountType || 'individual',
            onboardingData: parsed.onboardingData,
            sessionToken: parsed.sessionToken,
            email: parsed.email,
          });
          setIsRestored(true);
          return;
        }
      }

      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const hasPassword = hasResumeCredential(parsed?.data);
        if (parsed?.data && parsed?.currentStep && hasPassword) {
          applyRestoredSession({
            currentStep: parsed.currentStep,
            accountType: parsed.accountType || parsed.data.accountType || 'individual',
            onboardingData: parsed.data,
            sessionToken: parsed.sessionToken,
            email: parsed.data.email as string | undefined,
          });
        } else if (!hasPassword) {
          // No password yet — clear storage so stale data never bleeds into a fresh start
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch { /* ignore parse errors */ }
    setIsRestored(true);
  }, [applyRestoredSession]);

  // Save to localStorage ONLY after a password has been created.
  // Before that point, a page refresh must always produce a clean slate.
  useEffect(() => {
    if (!isRestored) return;

    // *** PERMANENT RULE: never persist before password is set ***
    const hasPassword = hasResumeCredential(data);
    if (!hasPassword) return;

    try {
      const toSave = {
        currentStep,
        accountType: data.accountType,
        data,
        sessionToken,
        savedAt: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (err) {
      console.error('Error saving to localStorage:', err);
    }
  }, [data, currentStep, sessionToken, isRestored]);

  // Auto-save to database when data or step changes
  useEffect(() => {
    // Skip early steps
    if (currentStep === 'welcome' || currentStep === 'account-type') {
      return;
    }

    // Determine available identifiers
    const hasEmail = Boolean(data.email);
    const hasPhone = Boolean(data.phoneNumber && data.countryCode);
    const hasCorporateCompany = data.accountType === 'corporate' && 
      data.corporateCompany?.registrationNumber;
    
    // Need at least one identifier to save progress
    // Individual: phone number (collected first) or email
    // Corporate: registration number or email
    if (!hasEmail && !hasPhone && !hasCorporateCompany) {
      return;
    }

    // Determine the email/identifier to use for saving
    // Priority: real email > phone placeholder > corporate placeholder
    let saveEmail = data.email;
    if (!saveEmail && hasPhone) {
      // Use phone as placeholder for individual flow before email is collected
      const cleanPhone = `${data.countryCode}${data.phoneNumber}`.replace(/[^0-9+]/g, '');
      saveEmail = `phone_${cleanPhone}@pending.local`;
    }
    if (!saveEmail && hasCorporateCompany) {
      saveEmail = `corp_${data.corporateCompany!.registrationNumber}@pending.local`;
    }

    if (!saveEmail) {
      return;
    }

    const dataToSave = {
      currentStep,
      accountType: data.accountType,
      onboardingData: data
    };
    
    const dataHash = JSON.stringify(dataToSave);
    if (dataHash === lastSaveRef.current) return;

    // Debounce saves
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const fullPhone = hasPhone ? `${data.countryCode}${data.phoneNumber}` : undefined;
        
        const { error } = await supabase.functions.invoke('save-onboarding-progress', {
          body: {
            email: saveEmail,
            session_token: sessionToken,
            current_step: currentStep,
            account_type: data.accountType,
            onboarding_data: data,
            // Include identifiers for lookup
            registration_number: data.corporateCompany?.registrationNumber,
            phone_number: fullPhone
          }
        });

        if (!error) {
          lastSaveRef.current = dataHash;
        } else {
          // Check if this is a duplicate identity block (409)
          const errBody = (error as any)?.context?.body;
          try {
            const parsed = typeof errBody === 'string' ? JSON.parse(errBody) : errBody;
            if (parsed?.duplicate_identity) {
              console.warn('[DUPLICATE-IDENTITY] Duplicate detected during auto-save:', parsed);
              setDuplicateBlock({
                message: parsed.error || 'An application or account already exists for this person.',
                email: parsed.email || null,
                action: parsed.action || 'login',
              });
              return;
            }
          } catch { /* not parseable */ }
          console.error('Failed to auto-save progress:', error);
        }
      } catch (err) {
        console.error('Error auto-saving progress:', err);
      }
    }, 1500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [data, currentStep, sessionToken]);

  const updateData = (updates: Partial<OnboardingData>) => {
    setData(prev => ({ ...prev, ...updates }));
  };

  const resetOnboarding = () => {
    // Clear localStorage
    localStorage.removeItem(STORAGE_KEY);
    // Reset state to initial values
    setData(initialData);
    setSessionToken(null);
    setCustomBackHandler(null);
    lastSaveRef.current = '';
  };

  const restoreSession = (sessionData: {
    currentStep: string;
    accountType: string;
    onboardingData: Record<string, unknown>;
    sessionToken: string;
    email: string;
  }) => {
    applyRestoredSession(sessionData);
  };

  // Get the appropriate steps based on account type
  const steps = data.accountType === 'corporate' ? corporateSteps : individualSteps;

  const stepIndex = steps.indexOf(currentStep);
  const totalSteps = steps.length - 2; // Exclude welcome and success

  const goToNextStep = () => {
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      let targetIndex = currentIndex + 1;

      // A step is "done" if the explicit flag is set OR if the user is already
      // past that step in the flow (handles sessions where flags weren't persisted).
      const phoneStepIdx  = Math.max(steps.indexOf('corporate-phone'), steps.indexOf('phone'));
      const emailStepIdx  = Math.max(steps.indexOf('corporate-email'), steps.indexOf('email'));
      const passcodeStepIdx = Math.max(steps.indexOf('corporate-passcode'), steps.indexOf('passcode'));

      const phoneVerified = Boolean(data.phoneOtpVerified && data.phoneNumber)
        || (currentIndex > phoneStepIdx && Boolean(data.phoneNumber));
      const emailVerified = Boolean(data.emailOtpVerified && data.email && !data.email.includes('@pending.local'))
        || (currentIndex > emailStepIdx && Boolean(data.email) && !data.email.includes('@pending.local'));
      const passcodeSet   = Boolean(data.passcode || sessionToken)
        || (currentIndex > passcodeStepIdx);

      // BaaS step is only shown when the applicant selected the "baas" service
      const isBaaSSelected = Boolean(data.corporateCompany?.servicesRequested?.includes('baas'));

      while (targetIndex < steps.length - 1) {
        const targetStep = steps[targetIndex];
        const shouldSkip =
          (targetStep === 'corporate-existing-check') ||
          (targetStep === 'corporate-phone'    && phoneVerified) ||
          (targetStep === 'corporate-email'    && emailVerified) ||
          (targetStep === 'corporate-passcode' && passcodeSet)   ||
          (targetStep === 'phone'              && phoneVerified) ||
          (targetStep === 'email'              && emailVerified) ||
          (targetStep === 'passcode'           && passcodeSet)   ||
          (targetStep === 'corporate-baas'     && !isBaaSSelected);
        if (shouldSkip) {
          targetIndex++;
        } else {
          break;
        }
      }

      setCurrentStep(steps[targetIndex]);
    }
  };

  const goToPreviousStep = () => {
    if (customBackHandler && customBackHandler()) {
      return;
    }
    
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      let targetIndex = currentIndex - 1;

      const phoneStepIdx  = Math.max(steps.indexOf('corporate-phone'), steps.indexOf('phone'));
      const emailStepIdx  = Math.max(steps.indexOf('corporate-email'), steps.indexOf('email'));
      const passcodeStepIdx = Math.max(steps.indexOf('corporate-passcode'), steps.indexOf('passcode'));

      const phoneVerified = Boolean(data.phoneOtpVerified && data.phoneNumber)
        || (currentIndex > phoneStepIdx && Boolean(data.phoneNumber));
      const emailVerified = Boolean(data.emailOtpVerified && data.email && !data.email.includes('@pending.local'))
        || (currentIndex > emailStepIdx && Boolean(data.email) && !data.email.includes('@pending.local'));
      const passcodeSet   = Boolean(data.passcode || sessionToken)
        || (currentIndex > passcodeStepIdx);

      const isBaaSSelected = Boolean(data.corporateCompany?.servicesRequested?.includes('baas'));

      while (targetIndex > 0) {
        const targetStep = steps[targetIndex];
        const shouldSkip =
          (targetStep === 'corporate-existing-check') ||
          (targetStep === 'corporate-phone'    && phoneVerified) ||
          (targetStep === 'corporate-email'    && emailVerified) ||
          (targetStep === 'corporate-passcode' && passcodeSet)   ||
          (targetStep === 'phone'              && phoneVerified) ||
          (targetStep === 'email'              && emailVerified) ||
          (targetStep === 'passcode'           && passcodeSet)   ||
          (targetStep === 'corporate-baas'     && !isBaaSSelected);
        if (shouldSkip) {
          targetIndex--;
        } else {
          break;
        }
      }
      
      setCurrentStep(steps[targetIndex]);
    }
  };

  return (
    <OnboardingContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        data,
        updateData,
        goToNextStep,
        goToPreviousStep,
        stepIndex,
        totalSteps,
        customBackHandler,
        setCustomBackHandler,
        restoreSession,
        resetOnboarding,
        sessionToken,
        duplicateBlock,
        clearDuplicateBlock: () => setDuplicateBlock(null),
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}
